/* eslint-disable react/no-unused-prop-types */
import * as maptilersdk from '@maptiler/sdk'
import {createPortal} from 'react-dom'
import {useEffect, useRef, useMemo, useImperativeHandle, forwardRef, memo} from 'react'
import {useMap} from '../hooks/useMap'
import {applyReactStyle} from '../utils/applyReactStyles'
import PropTypes from 'prop-types'

// Adapted from https://github.com/mapbox/mapbox-gl-js/blob/v1.13.0/src/ui/popup.js

const createPopupInstance = ({
  offset,
  anchor,
  maxWidth,
  className,
  closeButton,
  props,
  longitude,
  latitude,
  onOpen
}) => {
  const popup = new maptilersdk.Popup({offset, anchor, maxWidth, className, closeButton, ...props})
  popup.setLngLat([longitude, latitude])
  popup.once('open', (e) => onOpen?.(e))
  return popup
}

const updatePopupProperties = (popup, {longitude, latitude, offset, anchor, maxWidth, className}) => {
  if (popup.getLngLat().lng !== longitude || popup.getLngLat().lat !== latitude) {
    popup.setLngLat([longitude, latitude])
  }
  if (offset && popup.options.offset !== offset) {
    popup.setOffset(offset)
  }
  if (popup.options.anchor !== anchor || popup.options.maxWidth !== maxWidth) {
    popup.options.anchor = anchor
    popup.setMaxWidth(maxWidth)
  }
  if (popup.options.className !== className) {
    updatePopupClassName(popup, popup.options.className, className)
    popup.options.className = className
  }
}

const updatePopupClassName = (popup, prevClassName, nextClassName) => {
  const prevClassList = new Set(prevClassName ? prevClassName.trim().split(/\s+/) : [])
  const nextClassList = new Set(nextClassName ? nextClassName.trim().split(/\s+/) : [])

  for (const c of prevClassList) {
    if (!nextClassList.has(c)) {
      popup.removeClassName(c)
    }
  }
  for (const c of nextClassList) {
    if (!prevClassList.has(c)) {
      popup.addClassName(c)
    }
  }
}

/**
 * Props for the Map component.
 * @typedef {Object} PopupExtraProps
 * @property {React.CSSProperties} [style] - Style object for the popup container.
 * @property {string} longitude - Longitude of the popup location.
 * @property {string} latitude - Latitude of the popup location.
 * @property {React.ReactNode} [children] - Child components to be rendered inside the map.
 * @property {function} [onOpen] - Callback function for when the popup opens.
 * @property {function} [onClose] - Callback function for when the popup closes.
 *
 * @typedef {import('@maptiler/sdk').PopupOptions & PopupExtraProps} PopupComponentProps
 */
/**

/**
 * A wrapper component for the maptilersdk.Popup that manages the lifecycle of the popup instance.
 * @type React.ForwardRefRenderFunction<HTMLButtonElement, PopupComponentProps>
 */

const Popup = forwardRef(
  (
    {
      children,
      longitude,
      latitude,
      onOpen,
      onClose,
      style,
      offset = 0,
      anchor,
      maxWidth,
      className,
      closeButton = false,
      ...props
    },
    ref
  ) => {
    const {map} = useMap()
    const container = useMemo(() => document.createElement('div'), [])
    const thisRef = useRef({props: {onOpen, onClose}})
    thisRef.current.props = {onOpen, onClose}

    const popupRef = useRef(null)

    const newPopup = useMemo(
      () =>
        createPopupInstance({
          offset,
          anchor,
          maxWidth,
          className,
          closeButton,
          props,
          longitude,
          latitude,
          onOpen: thisRef.current.props.onOpen
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    useEffect(() => {
      const handleClose = (e) => {
        thisRef.current.props.onClose?.(e)
      }

      newPopup.on('close', handleClose)
      newPopup.setLngLat({lng: longitude, lat: latitude}).setDOMContent(container).addTo(map)

      popupRef.current = newPopup

      return () => {
        newPopup.off('close', handleClose)
        if (newPopup.isOpen()) {
          newPopup.remove()
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Expose the popup instance via ref
    useImperativeHandle(ref, () => popupRef.current, [])

    // Apply style updates
    useEffect(() => {
      applyReactStyle(popupRef.current?.getElement(), style)
    }, [style])

    // Update popup properties if necessary
    if (popupRef.current?.isOpen()) {
      updatePopupProperties(popupRef.current, {longitude, latitude, offset, anchor, maxWidth, className})
    }

    return createPortal(children, container)
  }
)

Popup.displayName = 'Popup'

Popup.propTypes = {
  longitude: PropTypes.number.isRequired,
  latitude: PropTypes.number.isRequired,
  children: PropTypes.node,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  style: PropTypes.object,
  offset: PropTypes.number,
  anchor: PropTypes.string,
  maxWidth: PropTypes.string,
  className: PropTypes.string,
  closeButton: PropTypes.bool,
  props: PropTypes.object
}

export default memo(Popup)

/**
 * List of functions and their purpose:
 *
 * - createPopupInstance: Creates a new popup instance with the specified properties and event handlers.
 * - updatePopupProperties: Updates the properties of an existing popup instance if they have changed.
 * - updatePopupClassName: Updates the class names of a popup instance to reflect changes.
 * - Popup: Manages the lifecycle of a Maptiler popup and renders its content using React portals.
 */
